import { PageWidget } from '@/lib';
import { Page } from '@/lib/components/core/pageItems/page';
import { DownloadEnvReportButton } from '@/lib/components/reports/downloadEnvReportButton';
import { ReviewEnvReportButton } from '@/lib/components/reports/reviewEnvReportButton';
import { BasicTable } from '@/lib/components/tables/BasicTable';
import { Roles, tokenContainsRole } from '@/lib/context';
import { useGetEnvironmentReports } from '@/lib/queries/reports';
import { BasicTableColumn, ReviewStatus } from '@/lib/types';
import { RapportageStatus, TeeltplanIndex } from '@/lib/types/domain';
import { Segmented, Space, Tooltip } from 'antd';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RapportageStatusFilter = 'definitief' | 'concept' | 'all';

export const EnvironmentReportsPage: React.FunctionComponent = () => {
  const { t } = useTranslation('reports');
  const updateHook = useRef(() => {});
  const [rapportageStatusFilter, setRapportageStatusFilter] = useState<RapportageStatusFilter>('definitief');

  const showReviewButton = tokenContainsRole([Roles.Administrator, Roles.Bevoegdgezag]);
  const showDownloadReportButton = tokenContainsRole([Roles.Administrator, Roles.Bevoegdgezag, Roles.Deelnemer, Roles.Dienstverlener, Roles.Toezichthouder]);
  const isAdmin = tokenContainsRole([Roles.Administrator]);

  const useLoadEntries = (params: Parameters<typeof useGetEnvironmentReports>[0]) => {
    let statusFilter: string[] | undefined;
    if (isAdmin) {
      statusFilter = getRapportageStatusFilterValues(rapportageStatusFilter);
    }

    return useGetEnvironmentReports({
      ...params,
      filterValues: {
        ...params?.filterValues,
        ...(statusFilter ? { rapportageStatus: statusFilter } : {}),
      },
    });
  };

  const columns: BasicTableColumn<TeeltplanIndex>[] = [
    { label: 'UO Nummer', dataIndex: 'deelnemer.uoNummer', sortable: false, filterable: true, width: 1 },
    { label: 'Bedrijfsnaam', dataIndex: 'deelnemer.bedrijfsNaam', sortable: false, filterable: true },
    { label: 'Straat', dataIndex: 'deelnemer.vestigingsStraat', sortable: false, filterable: true },
    {
      label: 'Huisnr',
      dataIndex: 'deelnemer.vestigingsNummer',
      sortable: false,
      filterable: true,
      width: 1,
      render: (item, record) => {
        return item + (record?.deelnemer.vestigingsNummerToevoeging ? ' ' + record?.deelnemer.vestigingsNummerToevoeging : '');
      },
    },
    { label: 'Plaats', dataIndex: 'deelnemer.vestigingsPlaats', sortable: false, filterable: true },
    { label: 'Registratiejaar', dataIndex: 'registratieJaar', sortable: false, filterable: true },
    {
      label: 'Dienstverlener',
      dataIndex: 'deskundige.bedrijfsNaam',
      sortable: false,
      filterable: true,
      render: (_, record) => record?.dienstverlenerNaam ?? '-',
    },
    {
      label: 'Keuring',
      dataIndex: 'review',
      render: review => {
        return (
          <Tooltip title={review?.reason}>
            {review === null || undefined || review.status == ReviewStatus.Onbekend ? 'Nog niet gekeurd' : ReviewStatus[review.status]}
          </Tooltip>
        );
      },
    },
    {
      label: 'Status',
      dataIndex: 'rapportageStatus',
      render: status => t(`status.${RapportageStatus[status as RapportageStatus]}`),
    },
    {
      key: 'actions',
      dataIndex: 'id',
      label: '',
      render: (_, record) => (
        <>
          {showDownloadReportButton && (
            <DownloadEnvReportButton reportData={{ id: record?.id, registratieJaar: record?.registratieJaar, bedrijfsNaam: record?.deelnemer?.bedrijfsNaam }} />
          )}
          {showReviewButton && record?.rapportageStatus === RapportageStatus.Definitief && (
            <ReviewEnvReportButton reportData={record} onOk={updateHook.current} />
          )}
        </>
      ),
    },
  ];

  return (
    <Page title={t('pages.environmentReports.title')}>
      <PageWidget>
        <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
          {isAdmin && (
            <Segmented<RapportageStatusFilter>
              value={rapportageStatusFilter}
              options={[
                { value: 'definitief', label: t('filters.definitief') },
                { value: 'concept', label: t('filters.concept') },
                { value: 'all', label: t('filters.all') },
              ]}
              onChange={setRapportageStatusFilter}
            />
          )}
          <BasicTable<TeeltplanIndex> loadEntries={useLoadEntries} columns={columns} updateHook={updateHook} />
        </Space>
      </PageWidget>
    </Page>
  );
};

const getRapportageStatusFilterValues = (filter: RapportageStatusFilter): string[] => {
  switch (filter) {
    case 'concept':
      return [RapportageStatus.ConceptHerberekening.toString()];
    case 'all':
      return [RapportageStatus.Definitief.toString(), RapportageStatus.ConceptHerberekening.toString()];
    case 'definitief':
      return [RapportageStatus.Definitief.toString()];
  }
};
